/**
 * growthIntel/environment — production-vs-preview-vs-local guard.
 *
 * Analytics (GA4/GTM/Clarity) and the first-party tracker must ONLY run on the
 * real production host. The Vercel preview (and any other host) is treated as
 * 'preview' so it can NEVER pollute production data.
 *
 * Production hosts are an explicit allow-list. Extra hosts can be excluded via
 * the `admin_settings` key `growth_intel.filters.excludedHosts`, mirrored into
 * the public settings so the check stays synchronous on the client.
 */

export type SiteEnv = 'production' | 'preview' | 'local';

/** The only hosts counted as production. */
export const PRODUCTION_HOSTS = ['cendien.com', 'www.cendien.com'];

/** Cached host allow/deny lists, hydrated from settings (optional). */
let cachedExcludedHosts: string[] = [];

/** Sets the extra excluded hosts (from admin_settings growth_intel.filters). */
export function setExcludedHosts(hosts: string[] | undefined): void {
  cachedExcludedHosts = Array.isArray(hosts) ? hosts.map((h) => String(h).toLowerCase().trim()).filter(Boolean) : [];
}

function currentHost(): string {
  if (typeof window === 'undefined') return '';
  return (window.location.hostname || '').toLowerCase();
}

/** Resolves the current site environment. Defaults safe: unknown host = preview. */
export function getSiteEnv(): SiteEnv {
  const host = currentHost();
  if (!host) return 'local';
  if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host.endsWith('.local')) return 'local';
  if (cachedExcludedHosts.includes(host)) return 'preview';
  if (PRODUCTION_HOSTS.includes(host)) return 'production';
  return 'preview';
}

/** True only on the real production host. Gate ALL analytics + tracking on this. */
export function isProduction(): boolean {
  return getSiteEnv() === 'production';
}

/** True for local dev hosts. */
export function isLocal(): boolean {
  return getSiteEnv() === 'local';
}